import React, { useState } from 'react';
import { Btn, DataTable, Loading, Msg, Section, Select, fmtDate, panelGet } from '../../kit';
import { Check, DateRange, Labeled } from '../emailKit';
import { tagCell, useTagOptions } from '../../tags/shared';
import { ATTR_LABELS, ATTR_OPTIONS, FunnelSteps, Step, num } from './shared';

/**
 * The campaign funnel for a cohort instead of a campaign: everyone carrying a
 * tag (custom or automatic), over every tracked mail they got in the window
 * — campaigns and the automatic lifecycle mails alike (GET email/tag_funnel).
 * Opened / clicked are people's unless "Include automated" (automated=1).
 */

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

export const TagFunnel: React.FC = () => {
  const opts = useTagOptions();
  const tags = [...opts.autoTags, ...opts.tags];
  const [tag, setTag] = useState('');
  const [from, setFrom] = useState(isoDay(new Date(Date.now() - 30 * 86400000)));
  const [to, setTo] = useState(isoDay(new Date()));
  const [attr, setAttr] = useState('30');
  const [automated, setAutomated] = useState(false);
  const [state, setState] = useState<{ data: any; loading: boolean; error: string }>(
    { data: null, loading: false, error: '' });
  const run = async () => {
    if (!tag) return;
    setState(s => ({ ...s, loading: true, error: '' }));
    const params: Record<string, string> = { tag, attr_days: attr };
    if (automated) params.automated = '1';
    if (from) params.from = from;
    if (to) params.to = to;
    const { data, error } = await panelGet('email', 'tag_funnel', params);
    setState({ data, loading: false, error });
  };
  const d: any = state.data || {};
  const steps: Step[] = d.steps || [];
  const label = opts.tagByKey[d.tag]?.label || d.tag;
  return (
    <Section title="Funnel by tag">
      <p className="text-xs text-grayscale-1100 -mt-1">
        Everyone carrying the tag, and what our e-mails to them in the window led to. A person counts once per
        step: e-mailed at least once, then any of those mails delivered, opened, clicked, followed to the site,
        and a sign-up or trial after one of them.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <Labeled label="Tag (custom or automatic)">
          <Select value={tag} onChange={setTag} options={['', ...tags.map(t => t.key)]}
            labels={{ '': 'Pick a tag…', ...Object.fromEntries(tags.map(t =>
              [t.key, `${t.grp ? `${t.grp} › ` : ''}${t.label}${t.n !== undefined ? ` (${num(t.n)})` : ''}`])) }} />
        </Labeled>
        <DateRange label="E-mailed between" from={from} to={to} onChange={(a, b) => { setFrom(a); setTo(b); }} />
        <Labeled label="Sign-ups and trials counted">
          <Select value={attr} onChange={setAttr} options={ATTR_OPTIONS} labels={ATTR_LABELS} />
        </Labeled>
        <span title="Off: opens and clicks by people only. On: every open and click SendGrid reported, instant reads, Apple MPP and bot clicks included.">
          <Check checked={automated} onChange={setAutomated}>Include automated</Check>
        </span>
        <Btn kind="primary" onClick={run} disabled={!tag || state.loading}>
          {state.loading ? 'Counting…' : 'Show the funnel'}
        </Btn>
      </div>
      {state.error && <Msg kind="error">{state.error}</Msg>}
      {state.loading && !state.data && <Loading />}
      {state.data && (
        <>
          <div className="text-xs text-grayscale-1100">
            “{label}” · {num(d.tagged)} people carry it today · e-mailed {d.from} → {d.to}
            {' · '}{d.automated ? 'every open and click counted' : 'opens and clicks by people only'}
          </div>
          <FunnelSteps steps={steps} head={{ label: 'tagged', n: Number(d.tagged || 0) }} />
          {(d.campaigns || []).length > 0 && (
            <DataTable rows={(d.campaigns || []).map((c: any) => ({
              ...c, ...Object.fromEntries((c.steps || []).map((s: Step) => [s.key, s.n])),
            }))} maxHeight="20rem" csvName={`tag_funnel_campaigns_${d.tag}`}
              columns={['created_at', 'name', 'people', 'delivered', 'opened', 'clicked', 'visited', 'signed_up', 'trial']}
              labels={{ created_at: 'Sent', name: 'Campaign / mail', people: 'Tagged people e-mailed', signed_up: 'Signed up' }}
              render={{ created_at: r => <span>{r.created_at ? fmtDate(r.created_at) : 'automatic'}</span> }} />
          )}
          <DataTable rows={d.people || []} maxHeight="28rem" csvName={`tag_funnel_people_${d.tag}`}
            columns={['email', 'name', 'sends', 'last_sent_at', 'delivered', 'opened', 'clicked', 'visited',
              'signed_up', 'trial', 'lifecycle']}
            labels={{ last_sent_at: 'Last e-mailed', signed_up: 'Signed up', lifecycle: 'Now' }}
            render={{ lifecycle: tagCell }}
            empty="Nobody with this tag was e-mailed in the window." />
        </>
      )}
    </Section>
  );
};
